// alertService.js
import Swal from 'sweetalert2';

/**
 * Detecta el tema activo leyendo el atributo data-theme del <html>.
 * Fallback a la preferencia del sistema. Así los Swal son responsive al tema
 * sin que el llamador tenga que pasar theme manualmente.
 */
export function getCurrentTheme() {
    const domTheme = document.documentElement.getAttribute('data-theme');
    if (domTheme === 'dark' || domTheme === 'light') return domTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getColorFromVariable(kindof = "--p", theme = 'dark') {
    const element = document.querySelector(`[data-theme="${theme}"]`) ?? document.documentElement;
    if (!element) {
        return null;
    }

    // Obtiene el valor de la variable CSS
    const confirmButtonColor = getComputedStyle(element)
        .getPropertyValue(kindof)
        .trim();

    return confirmButtonColor;


}

const oklchFromTheme = (variable, theme, fallback) => {
    const color = getColorFromVariable(variable, theme);
    return color ? `oklch(${color})` : fallback;
};

const getThemeOptions = (theme) => {
        const options = {
            background: oklchFromTheme('--b1', theme, theme === 'dark' ? '#343a40' : '#fff'),
            color: oklchFromTheme('--bc', theme, theme === 'dark' ? '#fff' : '#000'),
            confirmButtonColor: oklchFromTheme('--s', theme, '#3085d6'),
            denyButtonColor: oklchFromTheme('--p', theme, '#6b7280'),
            cancelButtonColor: oklchFromTheme('--n', theme, '#6b7280'),
        }

        return options;
    }
;



// Función para mostrar una alerta general
export const showAlert = ({
                              title,
                              text,
                              icon,
                              theme = getCurrentTheme(),
                              confirmButtonText = 'Aceptar',
                              showCancelButton = false,
                              cancelButtonText = 'Cancelar',
                              ...restOptions // Permite pasar cualquier otra opción adicional de SweetAlert2
                          }) => {
    const themeOptions = getThemeOptions(theme);
    const options = {
        title,
        text,
        icon,
        confirmButtonText,
        showCancelButton,
        cancelButtonText,
        ...themeOptions,
        ...restOptions
    };

    return Swal.fire(options);
};

// Función para mostrar una alerta de error de forma sencilla
export const showErrorAlert = ({
                                   title = 'Error',
                                   text,
                                   theme = getCurrentTheme(),
                                   confirmButtonText = 'Aceptar',
                                   ...restOptions
                               }) => {
    return showAlert({
        title,
        text,
        icon: 'error',
        theme,
        confirmButtonText,
        ...restOptions
    });
};

// Alerta de éxito sencilla
export const showSuccessAlert = ({
                                     title = 'Hecho',
                                     text,
                                     theme = getCurrentTheme(),
                                     confirmButtonText = 'Aceptar',
                                     ...restOptions
                                 }) => {
    return showAlert({ title, text, icon: 'success', theme, confirmButtonText, ...restOptions });
};

// Confirmación genérica que devuelve el resultado completo de Swal
// (útil para diálogos con confirm/deny/cancel). Respeta el tema automáticamente.
export const showConfirm = ({
                                theme = getCurrentTheme(),
                                ...restOptions
                            }) => {
    const themeOptions = getThemeOptions(theme);
    return Swal.fire({ ...themeOptions, ...restOptions });
};

// Función para confirmar una acción (por ejemplo, borrar un elemento)
// Devuelve una promesa que se resuelve en true si se confirmó la acción
export const confirmDelete = async ({
                                        title = '¿Estás seguro?',
                                        text = 'Esta acción no se puede revertir',
                                        theme = getCurrentTheme(),
                                        confirmButtonText = 'Sí, eliminar',
                                        cancelButtonText = 'Cancelar',
                                        ...restOptions
                                    } = {}) => {


    const themeOptions = getThemeOptions(theme);
    const options = {
        title,
        text,

        icon: 'warning',
        ...themeOptions,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        ...restOptions
    };


    // const colorillo = getColorFromVariable('--s', theme);
    // options.confirmButtonColor = colorillo;
    const result = await Swal.fire(options);
    return result.isConfirmed;
};



/**
 * Muestra una alerta con un input para solicitar un nuevo nombre.
 *
 * @param {Object} params
 * @param {string} [params.currentName=''] - Nombre actual que se mostrará en el input.
 * @param {string} [params.theme='light'] - Tema a utilizar ('light' o 'dark').
 * @param {string} [params.title='Cambiar nombre'] - Título de la alerta.
 * @param {string} [params.text='Ingresa el nuevo nombre para el modelo:'] - Texto descriptivo de la alerta.
 * @returns {Promise<string|null>} El nuevo nombre ingresado o null si se cancela.
 */
export const askForName = async ({
                                     previousName = '',
                                     currentName = '',
                                     theme = getCurrentTheme(),
                                     title = 'Cambiar nombre',
                                     text = 'Ingresa el nuevo nombre para el modelo:',
                                     confirmButtonText = 'Actualizar',
                                     cancelButtonText = 'Cancelar',
                                 } = {}) => {
    // Obtiene las opciones de tema definidas
    const themeOptions = getThemeOptions(theme);

    // const themeContext  = getThemeContext(); // Obtener el tema actual del contexto
    // console.log("themeContext", themeContext);
    // const inputStyle = theme === 'dark'
    //     ? 'background: #343a40; color: #fff;'
    //     : 'background: #fff; color: #212529; border: 1px solid #ced4da;';

    const { value: newName } = await Swal.fire({
        title,
        text,
        input: 'text',
        inputLabel: 'Nuevo nombre',
        // Si previousName existe se usa como valor inicial, sino currentName
        inputValue: previousName || currentName,
        // Opcionalmente, puedes definir un placeholder
        inputPlaceholder: previousName ? '' : 'Ingresa el nombre actual',
        showCancelButton: true,
        confirmButtonText: confirmButtonText,
        cancelButtonText: cancelButtonText,
        inputValidator: (value) => {
            if (!value) {
                return '¡Debes ingresar un nombre!';
            }
            return null;
        },
        inputAttributes: {
            style: theme === "dark" ? 'background: #343a40; color: #fff;' :  'background: white; color: #343a40;'
        },
        ...themeOptions
    });

    return newName || null;
};
